/**
 * FunnelWidget — analytics-v2 funnel (ADS-053 / CORE-958).
 *
 * Fetches from GET /analytics-v2/funnel with the currently-selected window +
 * source, renders the six-stage funnel with drop-off percentages, plus a
 * reconciliation panel (GA4 vs DB purchase counts + delta) so operators can
 * spot data-quality drift at a glance.
 *
 * All shell concerns (PROD badge, window selector, attribution caption,
 * loading/error triad, source-degradation banner, ErrorBoundary) live in
 * AnalyticsV2Widget. This file owns only the widget-specific body (the
 * stage table + reconciliation panel) and the source-selector control.
 *
 * Null handling is load-bearing:
 *   - `stage.count === null` → "—" (stage doesn't apply for this segment,
 *     e.g. ad_click for non-paid_meta). NOT rendered as "0".
 *   - `stage.dropOffPercent === null` → "—" (first stage OR previous stage
 *     had a zero denominator).
 *   - `reconciliation.ga4PurchaseCount === null` → "—" (GA4 was silent per
 *     the CORE-999 graceful-degradation contract; distinct from a real 0).
 *   - `reconciliation.deltaPercent === null` → "N/A" (dbPurchaseCount is
 *     zero, so no meaningful denominator — NOT "in alignment"; see the
 *     FunnelReconciliation.deltaPercent type doc for the invariant).
 */

import { useState } from 'react';
import { getAnalyticsFunnel } from '@/api/client';
import { formatCount, formatDelta, formatPercent } from '@/utils/format';
import { degradedSourceLabels } from '@/utils/sourceStatuses';
import { useAnalyticsV2Fetch } from '@/hooks/useAnalyticsV2Fetch';
import { AnalyticsV2Widget } from './AnalyticsV2Widget';
import { AnalyticsV2ErrorBoundary } from './AnalyticsV2ErrorBoundary';
import type { FunnelSegment, FunnelWindow } from '@/types/api';

const DESCRIPTION = (
  <>
    <p>
      Visitor drop-off from ad click to purchase, per stage. Drop-off is the
      percentage lost between the previous stage and this one.
    </p>
    <p>
      A <strong>negative</strong> drop-off means the following stage recorded{' '}
      <em>more</em> events than the one before — an attribution imperfection
      (e.g. events firing without a preceding pageview), not an error.
    </p>
    <p>
      Reconciliation compares GA4&apos;s Purchase count to the DB. The DB is
      authoritative (plan §3.1); the delta measures GA4 undercount.
    </p>
  </>
);

const SOURCE_OPTIONS: readonly { value: FunnelSegment; label: string }[] = [
  { value: 'all', label: 'All sources' },
  { value: 'paid_meta', label: 'Paid — Meta' },
  { value: 'paid_google', label: 'Paid — Google' },
  { value: 'direct', label: 'Direct' },
  { value: 'organic', label: 'Organic' },
];

export function FunnelWidget(): JSX.Element {
  // State lives here (not inside FunnelWidgetBody) so it's visible above
  // the ErrorBoundary — the boundary needs it to compute `resetKey`, and
  // FunnelWidgetBody needs it as fetch deps. Same value on both sides.
  const [selectedWindow, setSelectedWindow] = useState<FunnelWindow>('7d');
  const [source, setSource] = useState<FunnelSegment>('all');

  return (
    <AnalyticsV2ErrorBoundary title="Funnel" resetKey={`${selectedWindow}:${source}`}>
      <FunnelWidgetBody
        selectedWindow={selectedWindow}
        setSelectedWindow={setSelectedWindow}
        source={source}
        setSource={setSource}
      />
    </AnalyticsV2ErrorBoundary>
  );
}

interface FunnelWidgetBodyProps {
  selectedWindow: FunnelWindow;
  setSelectedWindow: (w: FunnelWindow) => void;
  source: FunnelSegment;
  setSource: (s: FunnelSegment) => void;
}

function FunnelWidgetBody({
  selectedWindow,
  setSelectedWindow,
  source,
  setSource,
}: FunnelWidgetBodyProps): JSX.Element {
  const { data, error, isLoading } = useAnalyticsV2Fetch(
    () => getAnalyticsFunnel(source === 'all' ? { window: selectedWindow } : { window: selectedWindow, source }),
    [selectedWindow, source],
  );

  const degraded = data === null ? [] : degradedSourceLabels(data.sourceStatuses);

  return (
    <AnalyticsV2Widget
      title="Funnel"
      selectedWindow={selectedWindow}
      onWindowChange={setSelectedWindow}
      data={data}
      error={error}
      isLoading={isLoading}
      degradedSources={degraded}
      description={DESCRIPTION}
      extraControls={
        <label>
          Source
          <select
            className="analytics-v2-widget-select"
            value={source}
            onChange={(e) => setSource(e.target.value as FunnelSegment)}
          >
            {SOURCE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
      }
    >
      {data !== null && (
        <>
          <div className="table-wrap">
            <table className="funnel-widget-stages">
              <thead>
                <tr>
                  <th>Stage</th>
                  <th className="funnel-widget-num">Count</th>
                  <th className="funnel-widget-num">Drop-off</th>
                </tr>
              </thead>
              <tbody>
                {data.stages.map((stage) => (
                  <tr key={stage.name}>
                    <td>{stage.displayName}</td>
                    <td className="funnel-widget-num">{formatCount(stage.count)}</td>
                    <td className="funnel-widget-num">{formatPercent(stage.dropOffPercent)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <section className="funnel-widget-reconciliation">
            <h4>Reconciliation</h4>
            <p className="funnel-widget-reconciliation-caption">
              DB is authoritative for purchases (plan §3.1). Delta measures GA4 undercount vs DB.
            </p>
            <dl>
              <div>
                <dt>DB purchases</dt>
                <dd>{data.reconciliation.dbPurchaseCount.toLocaleString()}</dd>
              </div>
              <div>
                <dt>GA4 purchases</dt>
                <dd>{formatCount(data.reconciliation.ga4PurchaseCount)}</dd>
              </div>
              <div>
                <dt>Delta</dt>
                <dd>{formatDelta(data.reconciliation.deltaPercent)}</dd>
              </div>
            </dl>
          </section>
        </>
      )}
    </AnalyticsV2Widget>
  );
}
